// IndexedDB schema versionado completo - Production Ready v3

const DB_NAME = 'MeetingCopilotDB';
const DB_VERSION = 1;

export async function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Store: meetings
      if (!db.objectStoreNames.contains('meetings')) {
        const meetingsStore = db.createObjectStore('meetings', { keyPath: 'id' });
        meetingsStore.createIndex('by_status', 'status', { unique: false });
        meetingsStore.createIndex('by_scheduled', 'scheduled_at', { unique: false });
      }

      // Store: participants
      if (!db.objectStoreNames.contains('participants')) {
        const participantsStore = db.createObjectStore('participants', { keyPath: 'id' });
        participantsStore.createIndex('by_meeting', 'meeting_id', { unique: false });
      }

      // Store: topics
      if (!db.objectStoreNames.contains('topics')) {
        const topicsStore = db.createObjectStore('topics', { keyPath: 'id' });
        topicsStore.createIndex('by_meeting_seq', ['meeting_id', 'seq'], { unique: false });
      }

      // Store: events
      if (!db.objectStoreNames.contains('events')) {
        const eventsStore = db.createObjectStore('events', { keyPath: 'id' });
        eventsStore.createIndex('by_meeting_time', ['meeting_id', 't_ms'], { unique: false });
        eventsStore.createIndex('by_meeting_type', ['meeting_id', 'event_type'], { unique: false });
      }

      // Store: audio_segments
      if (!db.objectStoreNames.contains('audio_segments')) {
        const segmentsStore = db.createObjectStore('audio_segments', { keyPath: ['meeting_id', 'seq'] });
        segmentsStore.createIndex('by_meeting', 'meeting_id', { unique: false });
      }

      // Store: blocks
      if (!db.objectStoreNames.contains('blocks')) {
        const blocksStore = db.createObjectStore('blocks', { keyPath: 'id' });
        blocksStore.createIndex('by_meeting_seq', ['meeting_id', 'seq'], { unique: false });
      }

      // Store: editor_blocks
      if (!db.objectStoreNames.contains('editor_blocks')) {
        const editorStore = db.createObjectStore('editor_blocks', { keyPath: 'id' });
        editorStore.createIndex('by_meeting_block', ['meeting_id', 'block_id', 'seq'], { unique: false });
      }

      // Store: outbox (CRÍTICO: índice by_meeting_sort)
      if (!db.objectStoreNames.contains('outbox')) {
        const outboxStore = db.createObjectStore('outbox', { keyPath: 'id' });
        outboxStore.createIndex('by_meeting', 'meeting_id', { unique: false });
        outboxStore.createIndex('by_meeting_state', ['meeting_id', 'state'], { unique: false });
        outboxStore.createIndex('by_meeting_sort', ['meeting_id', 'sort_key'], { unique: false });
      }

      // Store: user_profiles (para offline login multi-usuario)
      if (!db.objectStoreNames.contains('user_profiles')) {
        db.createObjectStore('user_profiles', { keyPath: 'username' });
      }

      // Store: auth_credentials (PBKDF2 por usuario)
      if (!db.objectStoreNames.contains('auth_credentials')) {
        db.createObjectStore('auth_credentials', { keyPath: 'username' });
      }

      // Store: transcripts (para preview local)
      if (!db.objectStoreNames.contains('transcripts')) {
        const transcriptsStore = db.createObjectStore('transcripts', { keyPath: 'id', autoIncrement: true });
        transcriptsStore.createIndex('by_meeting_time', ['meeting_id', 'start_ms'], { unique: false });
      }

      console.log('✅ IndexedDB schema created/upgraded to version', DB_VERSION);
    };
  });
}

// Helper: Execute transaction with promise
export async function executeTransaction<T>(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const request = operation(store);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Helper: Get all from index
export async function getAllFromIndex<T>(
  db: IDBDatabase,
  storeName: string,
  indexName: string,
  query?: IDBKeyRange | IDBValidKey
): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const index = store.index(indexName);
    const request = query ? index.getAll(query) : index.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Global DB instance holder
let dbInstance: IDBDatabase | null = null;

export async function getDB(): Promise<IDBDatabase> {
  if (!dbInstance) {
    dbInstance = await initDB();
  }
  return dbInstance;
}

